import React, { useEffect, useRef, useState } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { CrmCommentHooks } from 'types/crm/comments'
import { ContactOwner } from 'types/crm/contacts'
import { getInitials } from 'utils/getInitials'
import { useAppSelector } from 'store'
import { selectAuthUser } from 'store/slices/auth/auth.selector'
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor'
import Spinner from 'components/Loaders/Spinner'
import CrmCommentThread from './CrmCommentThread'

interface CrmCommentsPanelProps {
    parentUuid: string
    entityUuid: string
    commentHooks: CrmCommentHooks
    mentionUsers?: ContactOwner[]
    onCommentAdded?: () => void
}

const CrmCommentsPanel: React.FC<CrmCommentsPanelProps> = ({
    parentUuid, entityUuid, commentHooks, mentionUsers = [], onCommentAdded
}) => {
    const currentUser = useAppSelector(selectAuthUser)
    const [newComment, setNewComment] = useState('')
    const { mutate: submitComment, isPending } = commentHooks.useAddComment()

    const {
        comments,
        isLoading,
        hasMore,
        isFetchingMore,
        onLoadMore,
        refetch,
    } = commentHooks.useRootComments(parentUuid, entityUuid)

    const sentinelRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!hasMore || !onLoadMore) return
        const node = sentinelRef.current
        if (!node) return
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) onLoadMore() },
            { threshold: 0.1 },
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [hasMore, onLoadMore])

    const isEmpty =
        !newComment ||
        newComment === '<p></p>' ||
        (newComment.replace(/<[^>]*>?/gm, '').trim() === '' && !newComment.includes('<img'))

    const handleAdd = () => {
        if (isEmpty || isPending) return
        submitComment(
            { parentUuid, entityUuid, commentText: newComment, parentCommentUuid: null },
            {
                onSuccess: () => {
                    setNewComment('')
                    refetch()
                    onCommentAdded?.()
                },
            },
        )
    }

    return (
        <div className="pt-2 min-w-0">
            <div className="flex gap-3">
                <div className="w-8 h-8 bg-primary-subtle rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {getInitials(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Y')}
                </div>
                <div className="flex-1 w-full border border-primary-subtle rounded-lg bg-primary-variant max-h-[300px] overflow-y-auto min-w-0">
                    <TipTapEditor
                        initialContent={newComment}
                        onChange={setNewComment}
                        placeholder="Add a comment... (Type @ to mention)"
                        className={isPending ? 'opacity-50 pointer-events-none min-h-[60px]' : 'min-h-[0px]'}
                        mentionUsers={mentionUsers}
                        isComment
                    />
                </div>
            </div>
            <div className="flex items-center justify-end mt-3 ml-11">
                <button
                    onClick={handleAdd}
                    disabled={isEmpty || isPending}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${!isEmpty && !isPending ? 'button-active' : 'button-inactive opacity-50 cursor-not-allowed'}`}
                >
                    {isPending ? 'Posting...' : 'Comment'}
                    {!isPending && <Send size={14} />}
                </button>
            </div>

            {isLoading ? (
                <div className="py-4"><Spinner /></div>
            ) : comments.length > 0 ? (
                <div className="mt-4 space-y-4 min-w-0">
                    {comments.map((c) => (
                        <CrmCommentThread
                            key={c.commentUuid}
                            comment={c}
                            parentUuid={parentUuid}
                            entityUuid={entityUuid}
                            commentHooks={commentHooks}
                            mentionUsers={mentionUsers}
                            onDeleted={() => { refetch(); onCommentAdded?.() }}
                            onEdited={onCommentAdded}
                        />
                    ))}
                    <div className="flex flex-col items-center gap-2 py-2">
                        {isFetchingMore && <Loader2 className="w-5 h-5 animate-spin text-neutral-medium" />}
                        {hasMore && <div ref={sentinelRef} className="h-1 w-full" />}
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default CrmCommentsPanel